import { Check, Clock, Workflow as FlowIcon } from 'lucide-react'
import type { WorkerDetail, WorkerOrderRecord } from '@shared/worker'
import { fmtPrice } from '../../lib/format'
import { cn } from '../../lib/utils'
import { stopStepsText } from './WorkerCard'

/**
 * The worker as a live flow: what starts it → each lot's entry at IBKR → that lot's
 * target and stop. Node states are IBKR's own order states (held, working, filled, cancelled).
 */

type NodeState = 'planned' | 'held' | 'working' | 'partial' | 'filled' | 'cancelled' | 'unknown'

const DEAD = new Set(['Cancelled', 'ApiCancelled', 'Inactive'])

function stateOf(o: WorkerOrderRecord | undefined, draft: boolean): NodeState {
  if (draft || !o) return 'planned'
  if (o.status === 'Filled' || (o.filled > 0 && o.filled >= o.quantity)) return 'filled'
  if (o.status === 'Unknown') return 'unknown'
  if (DEAD.has(o.status)) return o.filled > 0 ? 'partial' : 'cancelled'
  if (o.filled > 0) return 'partial'
  if (o.status === 'PreSubmitted' || o.status === 'ApiPending' || o.status === 'PendingSubmit') return 'held'
  return 'working'
}

const STATE_TEXT: Record<NodeState, string> = {
  planned: 'planned',
  held: 'held (waits for its entry)',
  working: 'working',
  partial: 'partly filled',
  filled: 'filled',
  cancelled: 'cancelled',
  unknown: 'not found at the broker',
}

function Node({
  title,
  sub,
  note,
  state,
  tone,
  tag,
}: {
  title: string
  sub?: string
  /** A wrapping extra line, e.g. where a stop is planned to move next. */
  note?: string
  state: NodeState
  tone?: 'up' | 'down'
  tag?: string
}) {
  const filled = state === 'filled'
  return (
    <div
      className={cn(
        'relative min-w-0 rounded-lg border bg-[var(--color-surface)] px-2.5 py-1.5 text-[11.5px] shadow-[var(--e-1)]',
        state === 'planned' || state === 'held' ? 'border-dashed border-[var(--line)]' : 'border-[var(--line-soft)]',
        filled && tone === 'up' && 'border-[color-mix(in_oklab,var(--color-up)_55%,transparent)]',
        filled && tone === 'down' && 'border-[color-mix(in_oklab,var(--color-down)_55%,transparent)]',
        state === 'cancelled' && 'opacity-45',
        state === 'unknown' && 'border-[var(--color-down)]',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={cn('num truncate font-medium text-fg', state === 'cancelled' && 'line-through')}>{title}</span>
        {tag && <span className="shrink-0 text-[9.5px] uppercase tracking-wide text-fg-faint">{tag}</span>}
      </div>
      <div className="mt-0.5 flex items-center gap-1 text-[10.5px] text-fg-subtle">
        {filled ? (
          <Check size={10} className={tone === 'down' ? 'text-down' : 'text-up'} />
        ) : state === 'working' || state === 'partial' ? (
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-fg-muted" />
        ) : state === 'held' ? (
          <Clock size={10} />
        ) : null}
        <span className="truncate">{sub ?? STATE_TEXT[state]}</span>
      </div>
      {note && <div className="num mt-0.5 text-[10.5px] leading-snug text-fg-muted">{note}</div>}
    </div>
  )
}

/**
 * Entry → exits connector. Nodes are ~44px tall and stacked 4px apart, so the entry and the
 * target sit on the 22px line and the stop below on the 70px line.
 */
function Link({ fork }: { fork?: boolean }) {
  const line = 'absolute bg-[var(--line)]'
  return (
    <div className="relative w-6 shrink-0" aria-hidden>
      <div className={cn(line, 'left-0 top-[22px] h-px w-1/2')} />
      <div className={cn(line, 'left-1/2 right-0 top-[22px] h-px')} />
      {fork && (
        <>
          <div className={cn(line, 'left-1/2 top-[22px] h-[48px] w-px')} />
          <div className={cn(line, 'left-1/2 right-0 top-[70px] h-px')} />
        </>
      )}
    </div>
  )
}

export function WorkerFlow({ w }: { w: WorkerDetail }) {
  const draft = w.status === 'draft' || w.status === 'rejected' || w.status === 'expired'
  const s = w.spec
  const latest = (lot: number, role: string) => {
    const rows = w.orders.filter((o) => o.lot === lot && o.role === role)
    return rows.filter((o) => !DEAD.has(o.status) && o.status !== 'Filled').at(-1) ?? rows.at(-1)
  }
  const closeRows = w.orders.filter((o) => o.role === 'close')
  const nowOf = (id: string) => (w.marketNow ?? w.summary.marketNow)?.find((x) => x.id === id)?.text
  const startText =
    s.start.kind === 'afterWorker'
      ? `after worker ${s.start.workerId} → ${s.start.event}`
      : s.start.kind === 'when' && (draft || w.status === 'waiting')
        ? 'waits for its start condition'
        : w.approvedAt
        ? `approved ${new Date(w.approvedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`
        : 'on approval'

  return (
    <div className="overflow-x-auto">
      <div className="flex min-w-[560px] items-stretch gap-0 py-1">
        {/* start */}
        <div className="w-[170px] shrink-0">
          <Node
            title="Start"
            sub={s.entry.conditions.length ? `${startText} · + ${s.entry.conditions.length} broker condition${s.entry.conditions.length === 1 ? '' : 's'}` : startText}
            note={s.start.kind === 'when' && (draft || w.status === 'waiting') ? nowOf('start') : undefined}
            state={w.status === 'waiting' ? 'held' : draft ? 'planned' : 'filled'}
            tone="up"
          />
        </div>
        {/* bus: start → every lot (all links sit on the 22px centre line of a node) */}
        <div className="relative w-3 shrink-0" aria-hidden>
          <div className="absolute left-0 right-0 top-[22px] h-px bg-[var(--line)]" />
        </div>
        {/* lots */}
        <div className="relative min-w-0 flex-1 space-y-2 pl-3">
          {w.plan.lots.map((pl, i) => {
            const lastRow = i === w.plan.lots.length - 1 && !closeRows.length
            const e = latest(pl.lot, 'entry')
            const tp = latest(pl.lot, 'tp')
            const sl = latest(pl.lot, 'sl')
            const rt = w.runtime?.lots.find((x) => x.lot === pl.lot)
            const verb = s.side === 'long' ? 'BUY' : 'SELL'
            // Plans from before entry tiers carry no per-lot entry — they all use the spec's entry.
            const en = pl.entry ?? { tier: 1, orderType: s.entry.orderType, ref: s.entry.limitPrice ?? s.entry.stopPrice ?? null }
            const px = en.orderType === 'MKT' ? 'MKT' : `${en.orderType} ${fmtPrice(en.ref)}`
            const addTag = en.tier > 1 ? `add ${en.tier - 1}` : undefined
            return (
              <div key={pl.lot} className="relative flex items-stretch">
                <div className="absolute -left-3 top-[22px] h-px w-3 bg-[var(--line)]" aria-hidden />
                {!lastRow && <div className="absolute -bottom-[30px] -left-3 top-[22px] w-px bg-[var(--line)]" aria-hidden />}
                <div className="w-[180px] shrink-0">
                  <Node
                    title={`L${pl.lot} · ${verb} ${pl.qty}`}
                    sub={
                      draft
                        ? px
                        : `${px} · ${STATE_TEXT[stateOf(e, false)]}${rt && rt.entryFilled > 0 && rt.entryFilled < pl.qty ? ` ${rt.entryFilled}/${pl.qty}` : ''}`
                    }
                    state={stateOf(e, draft)}
                    tone="up"
                    tag={addTag}
                  />
                </div>
                <Link fork={!!pl.tp} />
                <div className="min-w-0 max-w-[280px] flex-1 space-y-1">
                  {pl.tp && (
                    <Node
                      title={`TP${pl.tp.tier} ${fmtPrice(tp?.limitPrice ?? rt?.target ?? pl.tp.price)}`}
                      sub={draft ? `sell ${pl.qty} limit` : STATE_TEXT[stateOf(tp, false)]}
                      state={stateOf(tp, draft)}
                      tone="up"
                      tag={tp?.planKey.includes('.r') ? 're-placed' : tp?.planKey.includes('.k') ? 'reduced' : undefined}
                    />
                  )}
                  <Node
                    title={`SL${pl.sl.tier} ${fmtPrice(sl?.stopPrice ?? rt?.stop ?? pl.sl.stop)}${pl.sl.trail ? ' trail' : ''}`}
                    sub={
                      draft
                        ? pl.sl.adjust
                          ? `→ ${fmtPrice(pl.sl.adjust.stop)} once ${fmtPrice(pl.sl.adjust.trigger)} trades`
                          : `stop ${pl.qty}`
                        : STATE_TEXT[stateOf(sl, false)]
                    }
                    note={stopStepsText(w, pl) || undefined}
                    state={stateOf(sl, draft)}
                    tone="down"
                    tag={sl?.planKey.includes('.r') ? 're-placed' : sl?.planKey.includes('.k') ? 'reduced' : sl && pl.sl.stop !== sl.stopPrice ? 'moved' : undefined}
                  />
                </div>
              </div>
            )
          })}
          {closeRows.map((o) => (
            <div key={o.id} className="relative flex items-stretch">
              <div className="absolute -left-3 top-[22px] h-px w-3 bg-[var(--line)]" aria-hidden />
              <div className="w-[180px] shrink-0">
                <Node title={o.lot ? `Reduce L${o.lot} · ${o.action} ${o.quantity} MKT` : `Close · ${o.action} ${o.quantity} MKT`} state={stateOf(o, false)} tone="down" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {s.rules.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {w.summary.rulesText.map((r) => {
            const fired = w.runtime?.firedRules.includes(r.id)
            return (
              <div
                key={r.id}
                title={r.text}
                className={cn(
                  'flex max-w-full items-start gap-1.5 rounded-lg border px-2 py-1 text-[11px]',
                  fired ? 'border-[var(--line)] text-fg' : 'border-dashed border-[var(--line-soft)] text-fg-subtle',
                )}
              >
                <FlowIcon size={11} className={cn('mt-[2px] shrink-0', fired ? 'text-up' : 'text-fg-faint')} />
                <span className="line-clamp-3">
                  <span className="font-medium">{r.id}</span> · {r.text}
                  {!fired && nowOf(r.id) && <span className="num block text-fg-subtle">now: {nowOf(r.id)}</span>}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
